import React, { useState } from 'react';
import { Row, Col, Table } from 'reactstrap';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTitle = (key) => {
  const titles = {
    trip_periodicity:    'Triplet Periodicity',
    max_readlength:      'Max Read Length',
    readlength_dist:     'Read Length Dist',
    mapped_reads_origin: 'Mapped Reads Origin',
    unmapped_reads:      'Unmapped Reads',
    clustering:          'Clustering',
  };
  return titles[key] || key;
};

const THRESHOLDS = {
  trip_periodicity:    { min: 0.6,  max: 1   },
  max_readlength:      { min: 25,   max: 30  },
  readlength_dist:     { min: 95,   max: 100 },
  mapped_reads_origin: { min: 75,   max: 100 },
  unmapped_reads:      { min: 0,    max: 20  },
};

const formatValue = (val) => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    return Number.isInteger(val) ? val : parseFloat(val.toFixed(3));
  }
  return val;
};

// ---------------------------------------------------------------------------
// Styles (plain objects — no extra CSS file or classnames needed)
// ---------------------------------------------------------------------------

  const C = {
    neutralBorder: '#dee2e6',
    neutralText: '#6c757d',
    white: '#fff',

    activePass: '#1A6D52',
    activePassGlow: 'rgba(26,109,82,.10)',

    activeWarn: '#E24B4A',
    activeWarnGlow: 'rgba(226,75,74,.10)',

    pass: '#639922',
    passBg: '#EAF3DE',
    passText: '#3B6D11',

    warn: '#E6A817',
    warnBg: '#FFF3CD',
    warnText: '#7d5800',

    warnSoftBg: '#FCEBEB',
    warnSoftText: '#A32D2D',
  };

const S = {
  // Summary cards
  card: (isActive, hasWarn) => ({
    cursor: 'pointer',
    borderRadius: 10,
    border: isActive
      ? `1.5px solid ${hasWarn ? C.warn : C.activePass}`
      : `1px solid ${C.neutralBorder}`,
    background: C.white,
    overflow: 'hidden',
    height: '100%',
    transition: 'border-color .15s, box-shadow .15s',
    boxShadow: isActive
      ? `0 0 0 3px ${hasWarn ? C.activeWarnGlow : C.activePassGlow}`
      : 'none',
  }),
  cardStripe: (hasWarn) => ({
    height: 3,
    background: hasWarn ? C.warn : C.pass,
  }),
  cardBody: {
    padding: '12px 14px 14px',
  },

  cardName: {
    fontSize: 11,
    fontWeight: 600,
    color: C.neutralText,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    marginBottom: 10,
    lineHeight: 1.35,
    minHeight: 28,
  },
  pillRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },

  pill: (type) => ({
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 20,
    background: type === 'pass' ? C.passBg : C.warnBg,
    color: type === 'pass' ? C.passText : C.warnText,
  }),
  warnBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: C.warnSoftText,
    background: C.warnSoftBg,
    padding: '1px 7px',
    borderRadius: 20,
    marginTop: 7,
  },

  // Detail panel
  detail: {
    background: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: 10,
    overflow: 'hidden',
  },
  detailHeader: {
    padding: '12px 18px 11px',
    borderBottom: '1px solid #dee2e6',
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 12,
  },
  detailTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#212529',
    margin: 0,
  },
  detailMeta: {
    fontSize: 12,
    color: '#6c757d',
  },
  thresholdBadge: {
    marginLeft: 'auto',
    fontSize: 11,
    color: '#6c757d',
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    padding: '2px 9px',
    borderRadius: 20,
  },
  cols: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  section: (right) => ({
    padding: '14px 18px',
    borderRight: right ? 'none' : '1px solid #dee2e6',
  }),
  secHead: (type) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    color: type === 'pass' ? C.passText : C.warnText,
    marginBottom: 10,
  }),
  dot: (type) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
    background: type === 'pass' ? C.pass : C.warn,
  }),
  sampleRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 0',
    borderBottom: '1px solid #f1f1f1',
    gap: 10,
    fontSize: 12,
  },
  sName: {
    flex: 1,
    fontWeight: 500,
    color: '#212529',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sVal: {
    color: '#6c757d',
    minWidth: 40,
    textAlign: 'right',
    flexShrink: 0,
  },
  barBg: {
    flex: 2,
    height: 4,
    background: '#e9ecef',
    borderRadius: 2,
    overflow: 'hidden',
  },
  barFill: (type, pct) => ({
    width: `${pct}%`,
    height: '100%',
    borderRadius: 2,
    background: type === 'pass' ? C.pass : C.warn,
  }),
  emptyMsg: {
    fontSize: 12,
    color: '#adb5bd',
    padding: '6px 0',
  },

  // Clustering table
  clusteringSection: {
    padding: '16px 18px',
  },
  clusteringHead: {
    fontSize: 13,
    fontWeight: 600,
    color: '#92650a',
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SampleBar = ({ value, maxValue, type }) => {
  const pct = maxValue > 0 ? Math.min(100, (value / maxValue) * 100) : 0;
  return (
    <div style={S.barBg}>
      <div style={S.barFill(type, pct)} />
    </div>
  );
};

const SampleList = ({ samples, type, maxValue }) => {
  if (!samples.length) {
    return <p style={S.emptyMsg}>{type === 'pass' ? 'No samples passed.' : 'No warnings.'}</p>;
  }
  return (
    <>
      {samples.map(([name, val], idx) => (
        <div key={idx} style={{ ...S.sampleRow, borderBottom: idx === samples.length - 1 ? 'none' : '1px solid #f1f1f1' }}>
          <span style={S.sName} title={name}>{name}</span>
          {typeof val === 'number' && (
            <SampleBar value={val} maxValue={maxValue} type={type} />
          )}
          <span style={S.sVal}>{formatValue(val)}</span>
        </div>
      ))}
    </>
  );
};

const ClusteringDetail = ({ activeData }) => (
  <div style={S.detail}>
    <div style={S.detailHeader}>
      <span style={S.detailTitle}>Clustering</span>
      <span style={S.detailMeta}>{activeData.warn.length} warning{activeData.warn.length !== 1 ? 's' : ''}</span>
    </div>
    <div style={S.clusteringSection}>
      {activeData.warn.length === 0 ? (
        <p style={{ fontSize: 13, color: '#6c757d', margin: 0 }}>
          No clustering warnings — all samples clustered as expected.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ fontWeight: 600, color: '#495057', borderBottom: '1px solid #dee2e6', padding: '8px 12px' }}>Sample</th>
                <th style={{ fontWeight: 600, color: '#495057', borderBottom: '1px solid #dee2e6', padding: '8px 12px' }}>Expected cluster</th>
                <th style={{ fontWeight: 600, color: '#495057', borderBottom: '1px solid #dee2e6', padding: '8px 12px' }}>Actual cluster</th>
              </tr>
            </thead>
            <tbody>
              {activeData.warn.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                  <td style={{ padding: '7px 12px', fontWeight: 500 }}>{item[0]}</td>
                  <td style={{ padding: '7px 12px', color: '#6c757d' }}>{item[1].join(', ')}</td>
                  <td style={{ padding: '7px 12px', color: '#92650a' }}>{item[2].join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  </div>
);

const StandardDetail = ({ metricKey, activeData, threshold }) => {
  const allVals = [...activeData.pass, ...activeData.warn]
    .map(([, v]) => v)
    .filter((v) => typeof v === 'number');
  const maxValue = allVals.length ? Math.max(...allVals) * 1.05 : 1;
  const total = activeData.pass.length + activeData.warn.length;
  const warnRate = total > 0 ? ((activeData.warn.length / total) * 100).toFixed(0) : 0;

  return (
    <div style={S.detail}>
      <div style={S.detailHeader}>
        <span style={S.detailTitle}>{formatTitle(metricKey)}</span>
        <span style={S.detailMeta}>
          {total} sample{total !== 1 ? 's' : ''} · {warnRate}% warn rate
        </span>
        {threshold && (
          <span style={S.thresholdBadge}>
            Pass: {threshold.min} – {threshold.max}
          </span>
        )}
      </div>
      <div style={S.cols}>
        <div style={S.section(false)}>
          <div style={S.secHead('pass')}>
            <span style={S.dot('pass')} />
            Pass — {activeData.pass.length}
          </div>
          <SampleList samples={activeData.pass} type="pass" maxValue={maxValue} />
        </div>
        <div style={S.section(true)}>
          <div style={S.secHead('warn')}>
            <span style={S.dot('warn')} />
            Warn — {activeData.warn.length}
          </div>
          <SampleList samples={activeData.warn} type="warn" maxValue={maxValue} />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const QCSummaryDashboard = ({ data }) => {
  const metrics = data?.result_dict || {};
  const metricKeys = Object.keys(metrics);


  if (!metricKeys.length) return <p className="text-muted">No QC data available.</p>;

  const [activeMetric, setActiveMetric] = useState(metricKeys[0]);
  const activeData = metrics[activeMetric];

  console.log("activeData is", metrics[activeMetric]);

  if (!activeData) return <p className="text-muted">activeData is empty.</p>;

  return (
    <div className="mt-3">
      {/* ── Summary cards ── */}
      <Row className="mb-3 g-2">
        {metricKeys.map((key) => {
          const passCount = metrics[key].pass.length;
          const warnCount = metrics[key].warn.length;
          const hasWarn   = warnCount > 0;
          const isActive  = activeMetric === key;
          const total     = passCount + warnCount;

          return (
            <Col xs={6} md={4} lg={2} key={key}>
              <div
                style={S.card(isActive, hasWarn)}
                onClick={() => setActiveMetric(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setActiveMetric(key)}
              >
                <div style={S.cardStripe(hasWarn)} />
                <div style={S.cardBody}>
                  <div style={S.cardName}>{formatTitle(key)}</div>
                  <div style={S.pillRow}>
                    {key !== 'clustering' && (
                      <span style={S.pill('pass')}>{passCount} pass</span>
                    )}
                    <span style={S.pill('warn')}>{warnCount} warn</span>
                  </div>
                  {key !== 'clustering' && hasWarn && total > 0 && (
                    <div style={S.warnBadge}>
                      ⚠ {((warnCount / total) * 100).toFixed(0)}% warned
                    </div>
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* ── Detail panel ── */}
      {activeMetric === 'clustering' ? (
        <ClusteringDetail activeData={activeData} />
      ) : (
        <StandardDetail metricKey={activeMetric} activeData={activeData} threshold={THRESHOLDS[activeMetric]} />
      )}
    </div>
  );
};

export default QCSummaryDashboard;